import React, { useState } from 'react';
import StatCard from '../../components/StatCard';
import {
  DollarSign,
  Calendar,
  Search,
  Filter,
  Eye,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  TrendingUp,
  TrendingDown,
  Activity,
  CreditCard,
  Building,
  User
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';

interface Payment {
  id: string;
  invoiceNumber: string;
  customer: string;
  customerType: 'aged_care' | 'ndis' | 'retail' | 'hospital';
  amount: number;
  dueDate: string;
  paidDate?: string;
  status: 'pending' | 'paid' | 'overdue' | 'partial' | 'disputed';
  paymentMethod: 'bank_transfer' | 'credit_card' | 'cheque' | 'cash' | 'eftpos';
  reference: string;
  daysOverdue: number;
  notes?: string;
}

interface PaymentSummary {
  totalOutstanding: number;
  totalPaid: number;
  overdueAmount: number;
  averageDaysToPay: number;
  paymentRate: number;
}

const PaymentTracking: React.FC = () => {
  const { user } = useAuth();
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCustomerType, setFilterCustomerType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<string>('all');

  const mockPayments: Payment[] = [
    {
      id: 'pay-001',
      invoiceNumber: 'INV-2024-001',
      customer: 'Sunnydale Aged Care',
      customerType: 'aged_care',
      amount: 12500.50,
      dueDate: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
      paidDate: format(subDays(new Date(), 2), 'yyyy-MM-dd'),
      status: 'paid',
      paymentMethod: 'bank_transfer',
      reference: 'TXN-789456123',
      daysOverdue: 0,
      notes: 'Payment received on time'
    },
    {
      id: 'pay-002',
      invoiceNumber: 'INV-2024-002',
      customer: 'NDIS Support Services',
      customerType: 'ndis',
      amount: 8750.25,
      dueDate: format(subDays(new Date(), 15), 'yyyy-MM-dd'),
      status: 'overdue',
      paymentMethod: 'bank_transfer',
      reference: 'PENDING',
      daysOverdue: 15,
      notes: 'Follow up required'
    },
    {
      id: 'pay-003',
      invoiceNumber: 'INV-2024-003',
      customer: 'John Smith (Retail)',
      customerType: 'retail',
      amount: 125.75,
      dueDate: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
      status: 'pending',
      paymentMethod: 'credit_card',
      reference: 'PENDING',
      daysOverdue: 0,
      notes: 'Payment due soon'
    },
    {
      id: 'pay-004',
      invoiceNumber: 'INV-2024-004',
      customer: 'Royal Hospital',
      customerType: 'hospital',
      amount: 25000.00,
      dueDate: format(subDays(new Date(), 10), 'yyyy-MM-dd'),
      paidDate: format(subDays(new Date(), 8), 'yyyy-MM-dd'),
      status: 'paid',
      paymentMethod: 'bank_transfer',
      reference: 'TXN-456789123',
      daysOverdue: 0,
      notes: 'Early payment received'
    }
  ];

  const paymentSummary: PaymentSummary = {
    totalOutstanding: mockPayments.filter(p => p.status === 'pending' || p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0),
    totalPaid: mockPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0),
    overdueAmount: mockPayments.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0),
    averageDaysToPay: 12,
    paymentRate: 85.5
  };

  const filteredPayments = mockPayments.filter(payment => {
    const matchesSearch = payment.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    const matchesCustomerType = filterCustomerType === 'all' || payment.customerType === filterCustomerType;
    
    let matchesDateRange = true;
    if (dateRange !== 'all') {
      const paymentDate = new Date(payment.dueDate);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24));
      
      switch (dateRange) {
        case 'overdue':
          matchesDateRange = daysDiff > 0;
          break;
        case 'due_soon':
          matchesDateRange = daysDiff >= -7 && daysDiff <= 0;
          break;
        case 'this_month':
          matchesDateRange = paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesCustomerType && matchesDateRange;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'partial': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'disputed': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'overdue': return <AlertTriangle className="h-4 w-4" />;
      case 'partial': return <Activity className="h-4 w-4" />;
      case 'disputed': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getCustomerTypeIcon = (type: string) => {
    switch (type) {
      case 'aged_care': return <Building className="h-4 w-4" />;
      case 'ndis': return <User className="h-4 w-4" />;
      case 'hospital': return <Building className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'bank_transfer': return <CreditCard className="h-4 w-4" />;
      case 'credit_card': return <CreditCard className="h-4 w-4" />;
      case 'cheque': return <FileText className="h-4 w-4" />;
      case 'cash': return <DollarSign className="h-4 w-4" />;
      case 'eftpos': return <CreditCard className="h-4 w-4" />;
      default: return <DollarSign className="h-4 w-4" />;
    }
  };

  const handleViewPayment = (paymentId: string) => {
    console.log(`Viewing payment ${paymentId}`);
  };

  const handleSendReminder = (paymentId: string) => {
    console.log(`Sending reminder for payment ${paymentId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Payment Tracking 💳
          </h1>
          <p className="text-gray-600 mt-2">Monitor payment status, track overdue accounts, and manage collections for {user?.branch}.</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Activity className="h-4 w-4" />
            <span>Send Reminders</span>
          </button>
        </div>
      </div>

      {/* Payment Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Overdue Amount"
          value={`$${paymentSummary.overdueAmount.toLocaleString()}`}
          icon={<AlertTriangle className="h-6 w-6" />}
          color="text-red-600"
          bgGradient="bg-gradient-to-br from-red-500 to-red-600"
        />
        <StatCard
          title="Outstanding"
          value={`$${paymentSummary.totalOutstanding.toLocaleString()}`}
          icon={<Clock className="h-6 w-6" />}
          color="text-yellow-600"
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
        />
        <StatCard
          title="Total Paid"
          value={`$${paymentSummary.totalPaid.toLocaleString()}`}
          icon={<CheckCircle className="h-6 w-6" />}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Payment Rate"
          value={`${paymentSummary.paymentRate}%`}
          icon={<TrendingUp className="h-6 w-6" />}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Avg Days to Pay"
          value={paymentSummary.averageDaysToPay}
          icon={<Calendar className="h-6 w-6" />}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      {/* Filters */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search invoices, customers..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="partial">Partial</option>
            <option value="disputed">Disputed</option>
          </select>

          <select
            value={filterCustomerType}
            onChange={(e) => setFilterCustomerType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Customer Types</option>
            <option value="aged_care">Aged Care</option>
            <option value="ndis">NDIS</option>
            <option value="hospital">Hospital</option>
            <option value="retail">Retail</option>
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Time</option>
            <option value="overdue">Overdue</option>
            <option value="due_soon">Due Soon</option>
            <option value="this_month">This Month</option>
          </select>

          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Payments List */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Payment Records</h3>
        <div className="space-y-4">
          {filteredPayments.map((payment) => (
            <div 
              key={payment.id} 
              className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                selectedPayment === payment.id ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedPayment(payment.id)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {getCustomerTypeIcon(payment.customerType)}
                    <span className="font-semibold text-gray-900">{payment.invoiceNumber}</span>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(payment.status)} flex items-center space-x-1`}>
                    {getStatusIcon(payment.status)}
                    <span>{payment.status.toUpperCase()}</span>
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">${payment.amount.toLocaleString()}</p>
                  {payment.daysOverdue > 0 && (
                    <p className="text-sm text-red-600">{payment.daysOverdue} days overdue</p>
                  )}
                </div>
              </div>
              
              <div className="mb-3">
                <p className="font-medium text-gray-900">{payment.customer}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                  <span className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span>Due: {format(new Date(payment.dueDate), 'dd/MM/yyyy')}</span>
                  </span>
                  {payment.paidDate && (
                    <span className="flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4" />
                      <span>Paid: {format(new Date(payment.paidDate), 'dd/MM/yyyy')}</span>
                    </span>
                  )}
                  <span className="flex items-center space-x-1">
                    {getPaymentMethodIcon(payment.paymentMethod)}
                    <span>{payment.paymentMethod.replace('_', ' ')}</span>
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Ref: {payment.reference}
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewPayment(payment.id);
                    }}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-200 flex items-center space-x-1"
                  >
                    <Eye className="h-3 w-3" />
                    <span>View</span>
                  </button>
                  {payment.status === 'overdue' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSendReminder(payment.id);
                      }}
                      className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700"
                    >
                      Send Reminder
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PaymentTracking;
